import type { MaybeRefOrGetter } from '@vueuse/core';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { ValueOf } from '@/helpers/types';
import type { ComputedRef, Ref } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';
import { toValue } from '@vueuse/core';
import { computed, onMounted, onServerPrefetch } from 'vue';

import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';
import { storeToRefs } from 'pinia';
import { useExperiment } from '@/libraries/experiment/composables/useExperiment';

// TYPES
type digits = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type day = `${'0'}${Exclude<digits, '0'>}` | `${'1' | '2'}${digits}` | '30' | '31';
type month = `${'0'}${Exclude<digits, '0'>}` | '10' | '11' | '12';
type year = `20${digits}${digits}`;
type ISO8601 = `${year}-${month}-${day}`;

/** Provider promotions are only for titles with Flatrate and free offers of the promo provider. */
const flatrateOrFreeOrAds = [MonetizationType.Flatrate, MonetizationType.Free, MonetizationType.Ads];

interface PromoText {
	/**
	 * Angled ribbon over square provider icons or
	 * drawer below wide provider icons.
	 */
	ribbon?: string;

	/**
	 * Ribbon text exclusively for wide provider icons
	 * which have room for a bit more text.
	 */
	wideRibbon?: string;

	/**
	 * Text below the square provider in GridOffer (next to 4k/quality).
	 * Text above the price in ListOffer.
	 */
	offerText?: string;

	/** Text that appears on the backdrop play button. */
	longText?: string;

	/** Optional promotion start date in `YYYY-MM-DD` format. */
	startDate?: ISO8601;

	/** `YYYY-MM-DD` format. This date is inclusive. */
	endDate: ISO8601;

	/** 3 character shortname of the promoted provider. */
	provider: string;

	/** The Url of the promoted provider which overrides the regular offer link. */
	url?: string;

	/** Disclaimer text - for additional Terms and Conditions, passed into the DisclaimerButton */
	disclaimerText?: string;

	/** Secondary disclaimer text for BuyBoxOfferNew - for additional Terms and Conditions, passed into the DisclaimerButton */
	offerDisclaimerText?: string;

	/** Suppress the provider promotion on the backdrop play button --> was a special requirement from HBO Max GB promotion to only have the buybox offer promotions, but assumption is that this could be more common */
	hideBackdropPromo?: boolean;

	experimentName?: string; // for testing purposes to link the promo to a specific experiment variant
}

/** Normal PromoText config with the Provider Icon URL */
export type PromoTextWithIcon = PromoText & { icon: string };

// Change uk to gb in the WebLocale list and return it upper case to match store country
type PromotionLocale = Uppercase<Exclude<keyof typeof WebLocale, 'uk'> | 'gb'>;

const promotions = {
	ES: [
		{
			ribbon: 'Ver oferta',
			startDate: '2026-09-10',
			endDate: '2026-09-30',
			provider: 'sst',
			longText: 'desde 4,99 € al mes durante 6 meses',
			disclaimerText:
				'La oferta finaliza el 12/10/2026. Plan Estándar por 4,99 € al mes durante 6 meses. Después, la suscripción se renovará automáticamente al precio mensual completo (actualmente 8,99 €), salvo cancelación. Cancela cuando quieras; la cancelación será efectiva al final del periodo de facturación. Solo para clientes nuevos y antiguos clientes que cumplan los requisitos. Se aplican términos y condiciones.',
			offerDisclaimerText:
				'La oferta finaliza el 12/10/2026. Plan Estándar por 4,99 € al mes durante 6 meses. Después, la suscripción se renovará automáticamente al precio mensual completo (actualmente 8,99 €), salvo cancelación. Cancela cuando quieras; la cancelación será efectiva al final del periodo de facturación. Solo para clientes nuevos y antiguos clientes que cumplan los requisitos. Se aplican términos y condiciones.',
		},
	],
	FR: [
		{
			ribbon: 'REGARDEZ',
			startDate: '2026-09-17',
			endDate: '2026-09-24',
			provider: 'dnp',
			longText: 'À partir de 5,99€/mois*',
			disclaimerText: '*Pour nouveaux abonnés 18+. Conditions sur DisneyPlus.com.',
			offerDisclaimerText: '*Pour nouveaux abonnés 18+. Conditions sur DisneyPlus.com.',
		},
	],
	US: [
		{
			ribbon: '',
			startDate: '2026-09-08',
			endDate: '2026-09-13',
			provider: 'hlu',
			longText: 'Sign up for Hulu now!',
			disclaimerText: 'Sign up to Hulu (with Ads) for $11.99/mo',
			offerDisclaimerText: 'Sign up to Hulu (with Ads) for $11.99/mo',
		},
	],
} satisfies Partial<Record<PromotionLocale, PromoText[]>>;

type PromotionCountries = keyof typeof promotions;
type PromotionProviders = ValueOf<typeof promotions>[number]['provider'];

const promotionProviderIcons: Record<PromotionProviders, string> = {
	amp: `${JW_CONFIG.IMAGESCALER_URL}/icon/322992749/s100/amazonprime.webp`,
	prv: `${JW_CONFIG.IMAGESCALER_URL}/icon/322992749/s100/amazonprime.webp`,
	hst: `${JW_CONFIG.IMAGESCALER_URL}/icon/324950024/s100/hotstar.webp`,
	dnp: `${JW_CONFIG.IMAGESCALER_URL}/icon/313118777/s100/disneyplus.webp`,
	atp: `${JW_CONFIG.IMAGESCALER_URL}/icon/152862153/s100/appletvplus.webp`,
	bng: `${JW_CONFIG.IMAGESCALER_URL}/icon/183470172/s100/binge.webp`,
	pmp: `${JW_CONFIG.IMAGESCALER_URL}/icon/242706661/s100/paramountplus.webp`,
	sst: `${JW_CONFIG.IMAGESCALER_URL}/icon/300772362/s100/skyshowtime.webp`,
	mxx: `${JW_CONFIG.IMAGESCALER_URL}/icon/305458112/s100/max.webp`,
	tbv: `${JW_CONFIG.IMAGESCALER_URL}/icon/313528601/s100/tubitv.webp`,
	cts: `${JW_CONFIG.IMAGESCALER_URL}/icon/48600036/s100/curiositystream.webp`,
	aho: `${JW_CONFIG.IMAGESCALER_URL}/icon/343788557/s100/amazonhbomax.webp`,
};

/** SSR Safe way to get active provider promotions. */
export function useProviderPromotion(title?: MaybeRefOrGetter<TitleDetail>): {
	titleHasProviderPromotion: ComputedRef<boolean>;
	titleHasBackdropPromo: ComputedRef<boolean>;
	isPromotionOffer: (offer: TitleOfferFragment) => boolean;
	providerPromotion: Ref<PromoTextWithIcon | null>;
} {
	const titleDetailsStore = useTitleDetailsStore();
	const { setProviderPromotion } = titleDetailsStore;
	const { providerPromotion } = storeToRefs(titleDetailsStore);

	const { activeVariantWithControlGroup: primePlayPromoVariant } = useExperiment('EXP_PRIME_PLAY_PROMO');

	onServerPrefetch(() => setProviderPromotion(toValue(title)));

	// refetch on client (no hydration error) to get the updated status
	onMounted(() => setProviderPromotion(toValue(title)));

	const titleHasProviderPromotion = computed(() => providerPromotion.value != null);
	const titleHasBackdropPromo = computed(() => {
		if (!titleHasProviderPromotion.value || providerPromotion.value?.hideBackdropPromo) return false;
		if (providerPromotion.value?.experimentName === 'EXP_PRIME_PLAY_PROMO')
			return primePlayPromoVariant.value === 'variant_1';
		return true;
	});

	function isPromotionOffer(offer: TitleOfferFragment) {
		return (
			flatrateOrFreeOrAds.includes(offer.monetizationType) &&
			(providerPromotion.value as PromoTextWithIcon | null)?.provider === offer.package.shortName
		);
	}

	return {
		/** Is a provider promotion applicable to the current title - typically be play button (backdrop) promotion, or updating offer texts and ribbons. */
		titleHasProviderPromotion,

		/** Is a provider promotion applicable to the backdrop play button. */
		titleHasBackdropPromo,

		/** Check if the offer is under the current title's provider promotion. */
		isPromotionOffer,

		/** The applicable promotion itself. */
		providerPromotion,
	};
}

export function getPromoText(
	country: string,
	title: TitleDetail | null = null,
	currentDate: Date = new Date()
): PromoTextWithIcon | null {
	const promos = promotions[country as PromotionCountries];

	if (!promos || promos.length === 0 || title == null || title.offers.length === 0) return null;

	const promoText = promos.find(promo =>
		title.offers.some(
			offer => flatrateOrFreeOrAds.includes(offer.monetizationType) && offer.package.shortName === promo.provider
		)
	);

	if (promoText == null) return null;
	// Checking if we're within the promo dates
	if (promoText.startDate && +currentDate < new Date(promoText.startDate).setUTCHours(0, 0, 0, 0)) return null;
	if (promoText.endDate && +currentDate > new Date(promoText.endDate).setUTCHours(23, 59, 59, 999)) return null;

	return { ...promoText, icon: promotionProviderIcons[promoText.provider] };
}
